import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import { initPlaceAutocomplete } from "../../../utils/googleMapsLoader";
import PageTitle from "../../layouts/PageTitle";

const ProjectAdd = () => {
 const navigate = useNavigate();
 const locationRef = useRef(null);

 const [loading, setLoading] = useState(false);
 const [clients, setClients] = useState([]);
 const [projectTypes, setProjectTypes] = useState([]);
 const [projectCategories, setProjectCategories] = useState([]);
 const [offices, setOffices] = useState([]);

 const [form, setForm] = useState({
 projectName: "",
 client: "",
 projectType: "",
 projectCategory: "",
 office: "",
 location: "",
 latitude: "",
 longitude: "",
 });

 /* -----------------------------
 LOAD CLIENTS
 ----------------------------- */
 useEffect(() => {
 api
 .get("/clients")
 .then((res) => {
 if (res.data.success) setClients(res.data.data);
 })
 .catch(() => {});
 }, []);

 /* -----------------------------
 GOOGLE PLACES AUTOCOMPLETE
 ----------------------------- */
 useEffect(() => {
 let mounted = true;

 initPlaceAutocomplete(locationRef, ({ formattedAddress, latitude, longitude }) => {
 if (!mounted) return;

 setForm((prev) => ({
 ...prev,
 location: formattedAddress || prev.location,
 latitude: latitude ?? prev.latitude,
 longitude: longitude ?? prev.longitude,
 }));
 }).catch((err) => {
 console.warn("Google Places init warning", err);
 });

 return () => {
 mounted = false;
 };
 }, []);
 /* -----------------------------
 LOAD PROJECT MASTERS
----------------------------- */
 useEffect(() => {
 const loadMasters = async () => {
 try {
 const [typeRes, categoryRes, officeRes] = await Promise.all([
 api.get("/project-types"),
 api.get("/project-categories"),
 api.get("/offices"),
 ]);

 if (typeRes.data.success) setProjectTypes(typeRes.data.data);

 if (categoryRes.data.success)
 setProjectCategories(categoryRes.data.data);

 if (officeRes.data.success)
 setOffices(officeRes.data.data);
 } catch (err) {
 swal("Error", "Failed to load project masters", "error");
 }
 };

 loadMasters();
 }, []);

 /* -----------------------------
 FETCH CURRENT LOCATION (GPS)
 ----------------------------- */
 const fetchCurrentLocation = () => {
 if (!navigator.geolocation) {
 swal("Error", "Geolocation is not supported", "error");
 return;
 }

 navigator.geolocation.getCurrentPosition(
 async (pos) => {
 const lat = pos.coords.latitude;
 const lng = pos.coords.longitude;

 setForm((prev) => ({
 ...prev,
 latitude: lat,
 longitude: lng,
 }));

 try {
 const res = await fetch(
 `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
 );
 const data = await res.json();

 const address =
 data.city ||
 data.locality ||
 data.principalSubdivision ||
 "Unknown Location";

 setForm((prev) => ({ ...prev, location: address }));

 swal("Success", "Location fetched successfully", "success");
 } catch {
 swal("Error", "Failed to fetch address", "error");
 }
 },
 () => swal("Error", "Unable to get location", "error"),
 );
 };

 /* -----------------------------
 HANDLE CHANGE
 ----------------------------- */
 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 /* -----------------------------
 SUBMIT
 ----------------------------- */
 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!form.projectName)
 return swal("Error", "Project Name is required", "error");
 if (!form.client) return swal("Error", "Client is required", "error");
 if (!form.projectType)
 return swal("Error", "Project Type is required", "error");
 if (!form.projectCategory)
 return swal("Error", "Project Category is required", "error");
 if (!form.office)
 return swal("Error", "Working Office is required", "error");

 setLoading(true);

 try {
 const res = await api.post("/projects", form);

 if (res.data.success) {
 swal("Success", "Project created successfully", "success");
 navigate("/projects");
 } else {
 swal("Error", res.data.message, "error");
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || err.message, "error");
 } finally {
 setLoading(false);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Add Project" motherMenu="Projects" />

 <Row>
 <Col lg={12}>
 <Card className="border-0 shadow-sm rounded-4">
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Add Project</Card.Title>
 <Button variant="secondary" onClick={() => navigate("/projects")}>
 Back
 </Button>
 </Card.Header>

 <Card.Body>
 <Form onSubmit={handleSubmit}>
 {/* BASIC DETAILS */}
 <h5 className="fw-bold mb-3">1. Basic Project Details</h5>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Name *</Form.Label>
 <Form.Control
 type="text"
 name="projectName"
 value={form.projectName}
 onChange={handleChange}
 placeholder="Enter project name"
 />
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Client *</Form.Label>
 <Form.Select
 name="client"
 value={form.client}
 onChange={handleChange}
 >
 <option value="">Select Client</option>
 {clients.map((c) => (
 <option key={c._id} value={c._id}>
 {c.name || c.clientName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 <Row className="mb-3">
 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Type *</Form.Label>
 <Form.Select
 name="projectType"
 value={form.projectType}
 onChange={handleChange}
 >
 <option value="">Select Type</option>
 {projectTypes.map((t) => (
 <option key={t._id} value={t._id}>
 {t.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Project Category *</Form.Label>
 <Form.Select
 name="projectCategory"
 value={form.projectCategory}
 onChange={handleChange}
 >
 <option value="">Select Category</option>
 {projectCategories.map((c) => (
 <option key={c._id} value={c._id}>
 {c.name}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>

 <Col md={6}>
 <Form.Group>
 <Form.Label>Working Office / Branch *</Form.Label>
 <Form.Select
 name="office"
 value={form.office}
 onChange={handleChange}
 >
 <option value="">Select Office</option>
 {offices.map((o) => (
 <option key={o._id} value={o._id}>
 {o.officeName} ({o.companyName})
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 </Row>

 {/* LOCATION */}
 <h5 className="fw-bold mt-4 mb-3">2. Location Details</h5>

 <div className="mb-3">
 <Button variant="info" onClick={fetchCurrentLocation}>
 Fetch Current Location
 </Button>
 </div>

 <div className="mb-3">
 <Form.Label>Location</Form.Label>
 <Form.Control
 type="text"
 name="location"
 value={form.location}
 onChange={handleChange}
 placeholder="Enter location"
 ref={locationRef}
 />
 <Form.Text className="text-muted">
 Type a place name to get suggestions and auto-fill latitude/longitude.
 </Form.Text>
 </div>

 <div className="mb-3">
 <Form.Label>Selected Location</Form.Label>
 <Form.Control
 type="text"
 value={form.location}
 readOnly
 placeholder="Your chosen location will appear here"
 />
 </div>

 <Row className="mb-4">
 <Col md={6}>
 <Form.Label>Latitude</Form.Label>
 <Form.Control value={form.latitude} readOnly />
 </Col>

 <Col md={6}>
 <Form.Label>Longitude</Form.Label>
 <Form.Control value={form.longitude} readOnly />
 </Col>
 </Row>

 {/* BUTTONS */}
 <div className="d-flex gap-2">
 <Button type="submit" variant="primary" disabled={loading}>
 {loading ? "Saving..." : "Save Project"}
 </Button>

 <Button
 variant="danger"
 onClick={() => navigate("/projects")}
 >
 Cancel
 </Button>
 </div>
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default ProjectAdd;
